'use client';

import { useEffect, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient } from '@/lib/api';
import { getInitData } from '@/lib/tma';

interface Stats {
  total_active_users: number;
  me: { bets_count: number; volume_usd_cents: number; profit_usd_cents: number; win_rate: number };
  percentiles: { bets: number | null; volume: number | null; profit: number | null; win_rate: number | null };
  averages: { bets: number | null; volume_cents: number | null; win_rate: number | null };
}

export function CommunityCompare({ refreshKey }: { refreshKey: number }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const api = createApiClient(getInitData);
    api
      .myCommunityStats()
      .then((r) => setStats(r))
      .catch((err: unknown) => {
        const e = err as ApiClientError;
        console.error('CommunityCompare failed', e?.code);
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="bg-bg-elev p-5 mb-4 flex justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (!stats) return null;
  if (stats.total_active_users < 3) {
    return (
      <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-line">
        <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em] mb-2">
          ● В СООБЩЕСТВЕ
        </div>
        <p className="text-text-tertiary text-sm">
          Сейчас активных юзеров: {stats.total_active_users}. Сравнения появятся когда сообщество вырастет.
        </p>
      </div>
    );
  }

  if (stats.me.bets_count === 0) {
    return null; // Юзеру без ставок не показываем.
  }

  const rows: Array<{
    label: string;
    percentile: number | null;
    myValue: string;
    avgValue: string | null;
    requiresResolution?: boolean;
  }> = [
    {
      label: 'Ставок',
      percentile: stats.percentiles.bets,
      myValue: String(stats.me.bets_count),
      avgValue: stats.averages.bets !== null ? stats.averages.bets.toFixed(1) : null,
    },
    {
      label: 'Объём',
      percentile: stats.percentiles.volume,
      myValue: `$${(stats.me.volume_usd_cents / 100).toFixed(0)}`,
      avgValue:
        stats.averages.volume_cents !== null
          ? `$${(stats.averages.volume_cents / 100).toFixed(0)}`
          : null,
    },
    {
      label: 'Прибыль',
      percentile: stats.percentiles.profit,
      myValue: `${stats.me.profit_usd_cents >= 0 ? '+' : '−'}$${(Math.abs(stats.me.profit_usd_cents) / 100).toFixed(2)}`,
      avgValue: null,
      requiresResolution: true,
    },
    {
      label: 'Win rate',
      percentile: stats.percentiles.win_rate,
      myValue: `${(stats.me.win_rate * 100).toFixed(0)}%`,
      avgValue:
        stats.averages.win_rate !== null
          ? `${(stats.averages.win_rate * 100).toFixed(0)}%`
          : null,
      requiresResolution: true,
    },
  ];

  const hasResolutions = stats.me.win_rate > 0 || stats.me.profit_usd_cents !== 0;

  return (
    <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-accent">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em]">
          ● В СООБЩЕСТВЕ
        </div>
        <span className="font-mono text-[10px] text-accent tracking-[0.05em] tabular-nums">
          {stats.total_active_users} АКТИВНЫХ
        </span>
      </div>

      <div className="space-y-3">
        {rows.map((r) => {
          const skip = r.requiresResolution && !hasResolutions;
          return (
            <div key={r.label} className="text-[12px]">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] text-text-tertiary tracking-[0.1em]">
                  {r.label.toUpperCase()}
                </span>
                <span className="font-bold tabular-nums">
                  {r.myValue}
                  {r.avgValue && (
                    <span className="text-text-tertiary font-normal ml-1.5 text-[11px]">
                      / ср. {r.avgValue}
                    </span>
                  )}
                </span>
              </div>
              {skip ? (
                <p className="font-mono text-[10px] text-text-tertiary tracking-[0.05em]">
                  ПОСЛЕ ПЕРВОЙ РЕЗОЛЮЦИИ
                </p>
              ) : r.percentile === null ? (
                <p className="font-mono text-[10px] text-text-tertiary tracking-[0.05em]">
                  НЕДОСТАТОЧНО ДАННЫХ
                </p>
              ) : (
                <PercentileBar pct={r.percentile} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PercentileBar({ pct }: { pct: number }) {
  const percentile = Math.round(pct * 100);
  const label = labelForPercentile(percentile);
  const colorClass =
    percentile >= 75
      ? 'bg-accent'
      : percentile >= 50
        ? 'bg-yes'
        : percentile >= 25
          ? 'bg-warn'
          : 'bg-text-tertiary';

  return (
    <>
      <div className="w-full h-1.5 bg-bg-elev2 overflow-hidden mb-1">
        <div className={`h-full ${colorClass} transition-all`} style={{ width: `${percentile}%` }} />
      </div>
      <p className="font-mono text-[10px] text-text-tertiary tracking-[0.05em]">
        ТЫ ЛУЧШЕ ЧЕМ {percentile}% ЮЗЕРОВ <span className="opacity-70">· {label.toUpperCase()}</span>
      </p>
    </>
  );
}

function labelForPercentile(p: number): string {
  if (p >= 90) return 'топ 10%';
  if (p >= 75) return 'топ 25%';
  if (p >= 50) return 'выше среднего';
  if (p >= 25) return 'около среднего';
  return 'ниже среднего';
}
